import { configuredPenpotOrigin } from './penpot-native.mjs';
import { preloadBlockSuiteRuntime } from './blocksuite-engine.mjs';

export const PENPOT_EDITOR_MODULE = '/vendor/penpot-editor-2.17.2/editor.js';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const failure = (code, message, cause) => Object.assign(new Error(message, cause ? { cause } : undefined), { code });

export function parsePenpotDocumentUrl(value, origin) {
  let url;
  try { url = new URL(value); } catch { throw failure('INVALID_PENPOT_URL', 'Penpot 디자인 파일 주소 전체를 입력해 주세요.'); }
  if (!origin || url.origin !== origin || url.username || url.password) throw failure('PENPOT_ORIGIN_MISMATCH', '현재 연결된 Penpot 서버의 디자인 주소를 입력해 주세요.');
  const [route, query = ''] = url.hash.slice(1).split('?');
  if (route !== '/workspace') throw failure('INVALID_PENPOT_URL', '파일과 페이지가 열린 Penpot 디자인 주소가 필요합니다.');
  const parameters = new URLSearchParams(query);
  const source = { origin };
  for (const [key, parameter] of [['teamId', 'team-id'], ['fileId', 'file-id'], ['pageId', 'page-id']]) {
    const values = parameters.getAll(parameter);
    if (values.length !== 1 || !UUID.test(values[0])) throw failure('INVALID_PENPOT_URL', '주소에 올바른 팀·파일·페이지 ID가 필요합니다.');
    source[key] = values[0];
  }
  return source;
}

function validateSource(source, origin) {
  if (!source || source.origin !== origin) throw failure('PENPOT_ORIGIN_MISMATCH', '이 디자인의 원본 서버가 현재 연결된 Penpot 서버와 다릅니다. 원본 서버 설정을 확인해 주세요.');
  for (const key of ['teamId', 'fileId', 'pageId']) {
    if (typeof source[key] !== 'string' || !UUID.test(source[key])) throw failure('INVALID_PENPOT_SOURCE', 'Penpot 원본의 팀·파일·페이지 ID가 올바르지 않습니다.');
  }
  if (source.boardId !== undefined && !UUID.test(source.boardId)) throw failure('INVALID_PENPOT_SOURCE', 'Penpot 보드 ID가 올바르지 않습니다.');
  return structuredClone(source);
}

/** Mount the compiled upstream editor. Its own state owns every design control. */
export function mountPenpotLibrary(host, {
  source = null, name = '새 디자인', onSourceChange, onChange, onStateChange, onStatus, onError,
  loadLibrary = () => import(PENPOT_EDITOR_MODULE),
  preloadRuntime = preloadBlockSuiteRuntime,
} = {}) {
  const document = host.ownerDocument;
  const view = document.defaultView;
  const wrapper = document.createElement('div');
  wrapper.className = 'es-penpot-library';
  wrapper.dataset.testid = 'studio-penpot-library';
  const element = document.createElement('div');
  element.className = 'es-penpot-library-root';
  element.dataset.testid = 'studio-penpot-mount';
  const recovery = document.createElement('div');
  recovery.className = 'es-penpot-recovery es-work-chrome'; recovery.hidden = true;
  const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = '다시 시도';
  const importing = document.createElement('details');
  const summary = document.createElement('summary'); summary.textContent = '기존 디자인 가져오기';
  const connection = document.createElement('form');
  connection.className = 'es-penpot-connect';
  const label = document.createElement('label'); label.textContent = '디자인 파일 주소';
  const input = document.createElement('input'); input.type = 'url'; input.required = true; input.autocomplete = 'off';
  input.setAttribute('aria-label', 'Penpot 디자인 파일 주소'); input.dataset.testid = 'studio-penpot-url';
  label.append(input);
  const connect = document.createElement('button'); connect.type = 'submit'; connect.textContent = '디자인 가져오기';
  connect.dataset.testid = 'studio-penpot-connect';
  const message = document.createElement('p'); message.setAttribute('role', 'status');
  const alert = document.createElement('p'); alert.setAttribute('role', 'alert'); alert.hidden = true;
  connection.append(label, connect); importing.append(summary, connection); recovery.append(retry, importing);
  wrapper.append(message, alert, recovery, element); host.append(wrapper);

  let origin = null, original = source === null ? null : structuredClone(source);
  let session = null, disposed = false, loading = false, connected = false, detached = false, attempt = 0;
  let state = null, lastError = null, pendingSource = false, createdFileId = null;
  const status = text => { message.textContent = text; message.hidden = connected; onStatus?.(text); };
  const report = error => {
    lastError = error; alert.textContent = error.message; alert.hidden = false;
    recovery.hidden = connected;
    status(connected ? '디자인 편집 중 오류가 발생했습니다' : '디자인을 열지 못했습니다'); onError?.(error);
  };
  const clearError = () => {
    if (!lastError) return;
    lastError = null; alert.textContent = ''; alert.hidden = true; recovery.hidden = true;
    status('디자인 편집 준비 완료');
  };
  function sourceFromState(value) {
    if (!original) throw failure('INVALID_PENPOT_STATE', '연결할 원본 디자인이 없습니다.');
    if (value.origin !== undefined && value.origin !== origin) throw failure('PENPOT_ORIGIN_MISMATCH', '편집기가 다른 Penpot 서버를 반환했습니다.');
    const next = { ...original };
    for (const key of ['teamId', 'fileId', 'pageId']) {
      if (value[key] !== undefined) {
        if (typeof value[key] !== 'string' || !UUID.test(value[key])) throw failure('INVALID_PENPOT_STATE', '편집기가 올바르지 않은 원본 ID를 반환했습니다.');
        next[key] = value[key];
      }
    }
    // The imported board belongs to its original page. Do not retain its ID
    // when the native editor switches to another page or file.
    if (next.fileId !== original.fileId || next.pageId !== original.pageId) delete next.boardId;
    return next;
  }
  function updateState(value, notifyRevision = true) {
    if (disposed || !value || typeof value !== 'object') return;
    const next = sourceFromState(value);
    const changedSource = JSON.stringify(next) !== JSON.stringify(original);
    const changedRevision = next.fileId === original.fileId && next.pageId === original.pageId
      && Number.isSafeInteger(state?.revision) && state.revision >= 0
      && Number.isSafeInteger(value.revision) && value.revision >= 0
      && value.revision !== state.revision;
    const changedState = JSON.stringify(state) !== JSON.stringify(value);
    state = structuredClone(value); original = next;
    if (changedSource && connected) onSourceChange?.(structuredClone(original));
    if (changedRevision && connected && notifyRevision) onChange?.();
    // Read-only commands also sample state; only actual native changes notify
    // the host, otherwise content_items -> onStateChange would recurse forever.
    if (changedState) onStateChange?.(structuredClone(state));
  }
  async function open(candidate) {
    if (disposed) throw failure('PENPOT_DISCONNECTED', '닫힌 디자인 편집기입니다.');
    if (loading) throw failure('BUSY', '디자인 편집기를 여는 중입니다.');
    const sequence = ++attempt;
    loading = true; connect.disabled = true; retry.disabled = true; alert.hidden = true; lastError = null;
    recovery.hidden = true; wrapper.setAttribute('aria-busy', 'true');
    status(candidate ? '디자인을 여는 중…' : '새 디자인을 준비하는 중…');
    try {
      if (candidate !== null) validateSource(candidate, origin);
      try { await preloadRuntime(); }
      catch (cause) { throw failure('BLOCKSUITE_LIBRARY_UNAVAILABLE', '문서 편집기를 불러올 수 없습니다. BlockSuite 라이브러리 빌드 산출물을 확인해 주세요.', cause); }
      if (disposed || sequence !== attempt) return;
      let library;
      try { library = await loadLibrary(); }
      catch (cause) { throw failure('PENPOT_LIBRARY_UNAVAILABLE', '디자인 편집기를 불러올 수 없습니다. 관리자에게 Penpot 라이브러리 Bazel 빌드와 서버 연결 확인을 요청해 주세요.', cause); }
      if (disposed || sequence !== attempt) return;
      if (typeof library.mount !== 'function') throw failure('PENPOT_LIBRARY_INVALID', '디자인 편집기 설치를 확인해 주세요.');
      let next = candidate;
      if (next === null) {
        if (typeof library.createDocument !== 'function') throw failure('PENPOT_LIBRARY_INVALID', '디자인 생성 기능을 불러올 수 없습니다. 편집기 라이브러리를 업데이트해 주세요.');
        next = await library.createDocument({ element, origin, name });
        if (disposed || sequence !== attempt) return;
      }
      next = validateSource(next, origin);
      if (candidate === null) createdFileId = next.fileId;
      if (JSON.stringify(next) !== JSON.stringify(original)) {
        original = next; pendingSource = true;
      }
      // Keep a created file even when its first mount fails. Retrying must open
      // that same file, not create another document in the user's project.
      if (pendingSource) { onSourceChange?.(structuredClone(original)); pendingSource = false; }
      const mounted = await library.mount({
        element, origin, teamId: next.teamId, fileId: next.fileId, pageId: next.pageId,
        newDocument: next.fileId === createdFileId,
        onChange: value => {
          if (disposed || sequence !== attempt) return;
          try { if (value && typeof value === 'object') updateState(value, false); if (connected) onChange?.(); }
          catch (error) { report(error); }
        },
        onStateChange: value => {
          if (disposed || sequence !== attempt) return;
          try { updateState(value); } catch (error) { report(error); }
        },
        onError: error => { if (!disposed && sequence === attempt) report(error instanceof Error ? error : new Error(String(error))); },
      });
      if (disposed || sequence !== attempt) { mounted?.dispose?.(); return; }
      for (const method of ['dispose', 'flush', 'getState', 'execute', 'attach', 'detach']) {
        if (typeof mounted?.[method] !== 'function') {
          mounted?.dispose?.();
          throw failure('PENPOT_LIBRARY_INVALID', `디자인 편집기의 ${method} 연결을 확인해 주세요.`);
        }
      }
      session = mounted; updateState(session.getState());
      if (lastError) throw lastError;
      connected = true; connection.hidden = true; recovery.hidden = true;
      if (detached) session.detach();
      status('디자인 편집 준비 완료');
    } catch (error) {
      if (!disposed && sequence === attempt) {
        session?.dispose(); session = null; connected = false;
        connection.hidden = false; report(error);
      }
    }
    finally {
      if (sequence === attempt) {
        loading = false; connect.disabled = false; retry.disabled = false; wrapper.removeAttribute('aria-busy');
      }
    }
  }
  retry.addEventListener('click', () => {
    if (loading || disposed) return;
    ready = open(original); void ready.catch(report);
  });
  connection.addEventListener('submit', event => {
    event.preventDefault();
    if (loading || disposed) return;
    try { ready = open(parsePenpotDocumentUrl(input.value.trim(), origin)); void ready.catch(report); }
    catch (error) { report(error); }
  });
  let ready;
  try {
    origin = configuredPenpotOrigin(view.location, document);
    if (!origin) throw failure('PENPOT_NOT_CONFIGURED', 'Penpot 서버가 설정되지 않았습니다. 원본 서버 설정을 확인해 주세요.');
    ready = open(original);
  } catch (error) { report(error); ready = Promise.resolve(); }
  return {
    get ready() { return ready; },
    getSource() { return original === null ? null : structuredClone(original); },
    getStatus() {
      return { ...structuredClone(state), origin, connected, loading, detached, selection: state?.selection ?? [], error: lastError ? { code: lastError.code || 'PENPOT_ERROR', message: lastError.message } : null };
    },
    async flush() {
      await ready;
      if (disposed || !connected || !session) throw lastError || failure('PENPOT_NOT_READY', '디자인 편집기를 준비하고 있습니다. 잠시 후 다시 시도해 주세요.');
      try {
        const value = await session.flush();
        updateState(value ?? session.getState());
        clearError();
        return structuredClone(state);
      } catch (error) {
        if (!disposed) report(error);
        throw error;
      }
    },
    async execute(command, input = {}) {
      if (disposed || !connected || !session) throw lastError || failure('PENPOT_NOT_READY', '디자인 편집기를 준비하고 있습니다. 잠시 후 다시 시도해 주세요.');
      const result = await session.execute(command, input);
      updateState(session.getState());
      return result;
    },
    attach(target) {
      if (disposed) throw failure('PENPOT_DISCONNECTED', '닫힌 디자인 편집기입니다.');
      detached = false; target.append(wrapper); session?.attach(element);
    },
    detach() { detached = true; session?.detach(); wrapper.remove(); },
    dispose() { if (disposed) return; disposed = true; attempt += 1; connected = false; session?.dispose(); session = null; wrapper.remove(); },
  };
}
